// Per-dashboard configuration: named groups of areas that get their own running
// total on the realtime maps ("Total Sentrum" inside the Oslo project).
//
// Stored on the project server-side (GET/PATCH /user-data/projects/:id/config,
// Trello #527). Two fallbacks keep the feature working everywhere:
//   - a deployment without the route (404) keeps using this device's localStorage,
//     exactly as v1 did, and says so in the editor;
//   - the first time the server has nothing for a project (`config: null`) and this
//     device does, the device copy is uploaded once and the local key removed.
// Everything else in the app talks to `dashboardConfigStore` only.

import { projectsApi } from "./projectsApi";

/** Which figures a total shows. Defaults to both when absent (v1 configs). */
export type GroupShow = "both" | "present" | "roaming";

export interface DashboardGroup {
  id: string;
  name: string;
  areaIds: string[];
  show?: GroupShow;
}

export interface DashboardConfig {
  groups: DashboardGroup[];
}

/** Where a dashboard's config is being read from and written to. */
export type ConfigStorage = "server" | "device";

const EMPTY: DashboardConfig = { groups: [] };
const key = (projectId: string) => `telcofy:dashboard-config:${projectId}`;

function readDevice(projectId: string): DashboardConfig {
  try {
    const raw = localStorage.getItem(key(projectId));
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<DashboardConfig>;
    return { groups: Array.isArray(parsed.groups) ? parsed.groups : [] };
  } catch {
    return EMPTY;
  }
}
function writeDevice(projectId: string, config: DashboardConfig) {
  try {
    localStorage.setItem(key(projectId), JSON.stringify(config));
  } catch {
    /* storage unavailable — the edit still applies for this session */
  }
}
function clearDevice(projectId: string) {
  try {
    localStorage.removeItem(key(projectId));
  } catch {
    /* ignore */
  }
}

const fromRemote = (config: Record<string, unknown> | null): DashboardConfig => ({
  groups: Array.isArray(config?.groups) ? (config!.groups as DashboardGroup[]) : [],
});

export const dashboardConfigStore = {
  async load(projectId: string): Promise<{ config: DashboardConfig; storage: ConfigStorage; error?: string }> {
    const device = readDevice(projectId);
    let remote: { available: boolean; config: Record<string, unknown> | null };
    try {
      remote = await projectsApi.getConfig(projectId);
    } catch (err) {
      // Server unreachable: show what this device has, but don't pretend it's saved.
      return { config: device, storage: "device", error: err instanceof Error ? err.message : "Could not load saved totals" };
    }
    if (!remote.available) return { config: device, storage: "device" };
    if (remote.config === null) {
      if (device.groups.length) {
        try {
          await projectsApi.patchConfig(projectId, { groups: device.groups });
          clearDevice(projectId);
        } catch (err) {
          return { config: device, storage: "device", error: err instanceof Error ? err.message : "Could not save totals" };
        }
      }
      return { config: device, storage: "server" };
    }
    return { config: fromRemote(remote.config), storage: "server" };
  },

  async save(projectId: string, config: DashboardConfig, storage: ConfigStorage): Promise<void> {
    if (storage === "server") await projectsApi.patchConfig(projectId, { groups: config.groups });
    else writeDevice(projectId, config);
  },
};

export const newGroupId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `g_${Date.now().toString(36)}`;
